import { ImportFailurePhase } from '../types/migration';

export type RetrySafety = 'safe' | 'review' | 'unknown';

export interface RetryGuidance {
    safety: RetrySafety;
    title: string;
    message: string;
}

/**
 * Classifies how safe it is to retry a failed import based on the phase in which it
 * failed (`Job3Import.failurePhase`). This gives the user an honest risk signal before
 * re-running the idempotent import:
 *
 * - Planning failure (terraform init/plan): no changes were applied to the target org,
 *   so a retry is safe.
 * - Execution failure (terraform apply): partial changes may already exist in the target
 *   org; a retry is idempotent (import-block adoption re-adopts existing resources) but the
 *   user should review the partial state first.
 * - Unknown phase (null/undefined): the phase could not be determined; advise caution.
 */
export function classifyRetrySafety(failurePhase: 1 | 2 | null | undefined): RetryGuidance {
    if (failurePhase === ImportFailurePhase.Planning) {
        return {
            safety: 'safe',
            title: 'Safe to retry',
            message:
                'This import failed during planning (terraform init/plan), so no changes were applied to the target org. Retrying re-runs the import from a clean state.'
        };
    }
    if (failurePhase === ImportFailurePhase.Execution) {
        return {
            safety: 'review',
            title: 'Review before retrying',
            message:
                'This import failed during execution (terraform apply), so some resources may already exist in the target org. Retrying is idempotent — already-created resources are adopted rather than duplicated — but review the partial state before continuing.'
        };
    }
    return {
        safety: 'unknown',
        title: 'Retry with caution',
        message:
            'The failure phase could not be determined, so the target org may contain partial changes. Retrying is idempotent, but review the migration before continuing.'
    };
}
